/**
 * Model Planner Export Service
 * Handles PDF generation for Model Planner data
 * Uses jsPDF for document generation
 *
 * REFACTORED: Config-driven, compact layout, smart filtering
 * - Only shows answered questions
 * - Skips "No" boolean gate questions entirely
 * - Uses smaller fonts and tighter spacing
 */

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import plannerConfig from '../config/modelPlannerQuestions.json';

/**
 * Helper function to get image data and dimensions
 * Handles both regular URLs and base64 data URLs
 */
async function fetchImageAsBase64(url) {
  try {

    // Check if it's already a base64 data URL
    if (url && url.startsWith('data:image')) {
      // Already base64 - just need to get dimensions
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            data: url,
            width: img.width,
            height: img.height
          });
        };
        img.onerror = () => {
          resolve(null);
        };
        img.src = url;
      });
    }

    // Regular URL - fetch and convert
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`Failed to fetch image: ${url}`);
      return null;
    }

    const blob = await response.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          resolve({
            data: reader.result,
            width: img.width,
            height: img.height
          });
        };
        img.onerror = () => {
          console.warn(`Failed to load image: ${url}`);
          resolve(null);
        };
        img.src = reader.result;
      };
      reader.onerror = () => {
        console.warn(`Failed to read blob for: ${url}`);
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(`Error fetching image ${url}:`, error);
    return null;
  }
}

/**
 * Get questions that should appear in report for an item
 * - Follows question branching (onYes/onNo paths)
 * - SKIPS boolean "No" gate questions entirely
 * - INCLUDES boolean "Yes" questions (as section indicators)
 * - INCLUDES all answered non-boolean questions
 */
function getAnsweredQuestionsForReport(config, sectionKey, itemData) {
  const section = config.sections[sectionKey];
  if (!section) return [];

  const questionOrder = section.questionOrder || Object.keys(section.questions);
  const result = [];
  let skipUntilQuestion = null;

  for (const qId of questionOrder) {
    const question = section.questions[qId];
    if (!question) continue;

    // Skip system questions (confirmation, add_another, etc.)
    if (qId.includes('confirmation') || qId.includes('add_another') || qId.includes('section_summary')) continue;

    // If skipping to a specific question, check if we've reached it
    if (skipUntilQuestion) {
      if (qId !== skipUntilQuestion) continue;
      skipUntilQuestion = null;
    }

    const fieldKey = question.field || qId;
    const answer = itemData[fieldKey];

    // Handle boolean gate questions
    if (question.type === 'boolean' || question.type === 'manager_decision') {
      if (answer === true) {
        // "Yes" - include and continue to next question (onYes path or next in order)
        result.push({ question, answer: true, fieldKey, type: 'boolean_yes' });
        // Continue to next question normally
      } else if (answer === false) {
        // "No" - SKIP entirely, jump to onNo path
        if (question.onNo && question.onNo !== 'SECTION_COMPLETE' && question.onNo !== 'LOOP_COMPLETE') {
          skipUntilQuestion = question.onNo;
        } else if (question.onNo === 'SECTION_COMPLETE' || question.onNo === 'LOOP_COMPLETE') {
          break; // Section/loop complete
        }
        continue; // Don't add to result
      } else {
        continue; // Not answered
      }
    }
    // Handle grouped_form - include if any field answered
    else if (question.type === 'grouped_form' && question.fields) {
      const answeredFields = question.fields.filter(f => {
        const val = itemData[f.field];
        // Check for arrays (images) or non-empty values
        if (Array.isArray(val)) return val.length > 0;
        return val !== undefined && val !== null && val !== '';
      });
      if (answeredFields.length > 0) {
        result.push({ question, answeredFields, itemData, type: 'grouped_form' });
      }
    }
    // Other types - include if answered
    else {
      if (answer !== undefined && answer !== null && answer !== '') {
        // Skip if it's just an empty array
        if (Array.isArray(answer) && answer.length === 0) continue;
        result.push({ question, answer, fieldKey, type: question.type });
      }
    }
  }

  return result;
}

/**
 * Add images to PDF document with responsive sizing
 * - Single image: larger, full width
 * - Multiple images: grid layout (2 per row)
 * Handles both { url: '...' } objects and plain URL strings
 */
async function addImagesToDocument(doc, images, yPosition, margin, contentWidth, pageHeight, checkPageBreak) {

  if (!images || !Array.isArray(images) || images.length === 0) {
    return yPosition;
  }

  // Normalize images to objects with url property
  // Handle both { url: '...' } objects and plain URL/base64 strings
  const normalizedImages = images.map(i => {
    if (typeof i === 'string') {
      return { url: i };
    }
    return i;
  });

  const validImages = normalizedImages.filter(i => i?.url);
  if (validImages.length === 0) return yPosition;

  // Responsive sizing based on image count
  const isSingleImage = validImages.length === 1;
  const baseWidth = isSingleImage
    ? Math.min(contentWidth - 4, 120)  // Full width for single, max 120mm
    : (contentWidth - 4) / 2;           // Half width for grid
  const maxImageHeight = isSingleImage ? 80 : 60;
  const gap = 3;

  let currentY = yPosition;

  for (let i = 0; i < validImages.length; i++) {
    const image = validImages[i];
    if (!image || !image.url) continue;

    const imageData = await fetchImageAsBase64(image.url);
    if (!imageData) continue;

    // Calculate aspect ratio and dimensions
    const aspectRatio = imageData.width / imageData.height;
    let imgWidth = baseWidth;
    let imgHeight = imgWidth / aspectRatio;

    // If too tall, scale down
    if (imgHeight > maxImageHeight) {
      imgHeight = maxImageHeight;
      imgWidth = imgHeight * aspectRatio;
    }

    // For single image, center it
    if (isSingleImage) {
      const xPos = margin + (contentWidth - imgWidth) / 2;
      const pageBreakOccurred = checkPageBreak(imgHeight + 5);
      if (pageBreakOccurred) {
        currentY = margin; // Reset to top of new page
      }
      try {
        doc.addImage(imageData.data, 'JPEG', xPos, currentY, imgWidth, imgHeight);
      } catch (error) {
        console.warn('Failed to add image to PDF:', error);
      }
      currentY += imgHeight + gap;
    } else {
      // Grid layout (2 per row)
      const isLeft = i % 2 === 0;
      const xPos = isLeft ? margin : margin + baseWidth + gap;

      // Check page break before placing image
      if (isLeft) {
        const pageBreakOccurred = checkPageBreak(imgHeight + 5);
        if (pageBreakOccurred) {
          currentY = margin; // Reset to top of new page
        }
      }

      try {
        doc.addImage(imageData.data, 'JPEG', xPos, currentY, imgWidth, imgHeight);
      } catch (error) {
        console.warn('Failed to add image to PDF:', error);
        continue;
      }

      // Move to next row after every 2 images or last image
      if (!isLeft || i === validImages.length - 1) {
        currentY += imgHeight + gap;
      }
    }
  }

  return currentY + 2;
}

/**
 * Map section keys to their data keys in sessionData
 */
const sectionDataKeyMap = {
  'ponds': 'ponds',
  'grading': 'gradingSections',
  'curb': 'curbTypes',
  'walls': 'walls',
  'ditches_swales': 'ditchesSwales',
  'sidewalks': 'sidewalks',
  'ada_ramps': 'adaRamps',
  'building_pads': 'buildingPads',
  'grate_elevations': 'grateElevations'
};

/**
 * Map section keys to their item name fields
 */
const itemNameFieldMap = {
  'ponds': 'pond_name',
  'curb': 'curb_type_name',
  'walls': 'wall_name_location',
  'ada_ramps': 'ramp_type'
};

class ModelPlannerExportService {
  /**
   * Generate PDF export from Model Planner session data
   * @param {Object} sessionData - Complete session data with all sections
   * @param {Object} project - Project information
   * @param {string} notes - Optional user notes
   * @returns {Promise<void>} - Downloads PDF file
   */
  async generatePDF(sessionData, project, notes = '') {
    const config = plannerConfig;

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter'
    });

    // COMPACT page settings
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Font sizes - compact
    const fontSize = {
      title: 14,
      sectionHeading: 10,
      itemHeading: 9,
      body: 8,
      small: 7,
      footer: 6
    };

    // Helper: Check if we need a new page
    const checkPageBreak = (neededSpace = 10) => {
      if (yPosition + neededSpace > pageHeight - margin - 5) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Helper: Add section heading (compact)
    const addSectionHeading = (title) => {
      checkPageBreak(12);
      yPosition += 4;

      doc.setFontSize(fontSize.sectionHeading);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(33, 37, 41);
      doc.text(title, margin, yPosition);
      yPosition += 4;

      // Thin underline
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.2);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 3;
    };

    // Helper: Add item name (for looped sections)
    const addItemHeading = (name) => {
      checkPageBreak(8);
      doc.setFontSize(fontSize.itemHeading);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(52, 58, 64);
      doc.text(name, margin + 2, yPosition);
      yPosition += 4;
    };

    // Helper: Add checkmark indicator for "Yes" boolean
    const addCheckmark = (text) => {
      checkPageBreak(5);
      doc.setFontSize(fontSize.body);
      doc.setTextColor(34, 139, 34); // Forest green
      const cleanText = text.replace(/\?$/, '').replace(/:$/, '');
      // Use bullet + "Yes" since checkmark character doesn't render in jsPDF
      doc.setFont('helvetica', 'bold');
      doc.text('YES', margin + 3, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const yesWidth = doc.getTextWidth('YES ');
      doc.text(`- ${cleanText}`, margin + 3 + yesWidth, yPosition);
      yPosition += 3;
    };

    // Helper: Add labeled value (compact inline format)
    const addLabeledValue = (label, value, indent = 3) => {
      // Skip N/A values as they're not meaningful
      if (value === 'N/A' || value === 'n/a' || value === 'NA') return;

      checkPageBreak(5);
      doc.setFontSize(fontSize.body);
      doc.setTextColor(60, 60, 60);

      const xPos = margin + indent;
      const cleanLabel = label.replace(/\?$/, '').replace(/:$/, '');

      // Format value
      let displayValue = value;
      if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No';
      } else if (Array.isArray(value)) {
        displayValue = value.join(', ');
      }
      displayValue = String(displayValue);

      // Skip if the final display value is N/A
      if (displayValue === 'N/A' || displayValue === 'n/a' || displayValue === 'NA') return;

      // Label in bold
      doc.setFont('helvetica', 'bold');
      const labelText = `${cleanLabel}: `;
      doc.text(labelText, xPos, yPosition);

      // Value in normal
      doc.setFont('helvetica', 'normal');
      const labelWidth = doc.getTextWidth(labelText);
      const maxValueWidth = contentWidth - indent - labelWidth - 2;
      const splitValue = doc.splitTextToSize(displayValue, maxValueWidth);
      doc.text(splitValue, xPos + labelWidth, yPosition);

      yPosition += splitValue.length * 3 + 2;
    };

    // Helper: Add grouped form fields
    const addGroupedFormFields = async (question, answeredFields, itemData) => {
      checkPageBreak(8);

      // Form title (smaller, subtle)
      doc.setFontSize(fontSize.small);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text(question.text || question.parentQuestion || '', margin + 3, yPosition);
      yPosition += 3;

      for (const field of answeredFields) {
        const value = itemData[field.field];
        if (value === undefined || value === null || value === '') continue;


        // Check if this looks like image data (either by field type or by value format)
        const isImageField = field.type === 'image';

        // Images can be: array of objects, array of strings, or a single string
        const isArrayOfImages = Array.isArray(value) && value.length > 0 &&
          (value[0]?.url || (typeof value[0] === 'string' && value[0].startsWith('data:image')));
        const isSingleImageString = typeof value === 'string' && value.startsWith('data:image');


        // Handle images - normalize to array format
        if (isImageField || isArrayOfImages || isSingleImageString) {
          let imageArray;
          if (Array.isArray(value)) {
            imageArray = value;
          } else if (typeof value === 'string') {
            // Could be base64 data URL or regular URL - wrap in array
            imageArray = [value];
          } else {
            imageArray = [];
          }

          if (imageArray.length > 0) {
            yPosition = await addImagesToDocument(
              doc, imageArray, yPosition, margin + 5,
              contentWidth - 5, pageHeight, checkPageBreak
            );
          }
        }
        // Handle other field types - but skip if it's image data being printed as text
        else if (!isImageField) {
          addLabeledValue(field.text || field.field, value, 5);
        }
      }
      yPosition += 1;
    };

    // ========== DOCUMENT HEADER (compact) ==========
    doc.setFontSize(fontSize.title);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(33, 37, 41);
    doc.text('MODEL PLANNER SUMMARY', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;

    // Project Info (compact single line format)
    doc.setFillColor(245, 248, 250);
    doc.rect(margin, yPosition, contentWidth, 18, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(margin, yPosition, contentWidth, 18, 'S');

    yPosition += 5;
    doc.setFontSize(fontSize.itemHeading);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(33, 37, 41);
    const jobNum = project?.jobNumber || project?.job_number || 'N/A';
    const projName = project?.name || project?.projectName || 'Untitled';
    const revDisplay = project?.rev && parseInt(project.rev) > 0
      ? (parseInt(project.rev) === 999 ? ' (Erosion)' : ` Rev ${project.rev}`)
      : '';
    doc.text(`${jobNum}${revDisplay} - ${projName}`, margin + 3, yPosition);

    yPosition += 4;
    doc.setFontSize(fontSize.small);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Exported: ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })}`, margin + 3, yPosition);

    yPosition += 12;

    // Notes (if provided, compact)
    if (notes && notes.trim()) {
      checkPageBreak(15);
      doc.setFillColor(255, 255, 235);
      const notesLines = doc.splitTextToSize(notes, contentWidth - 6);
      const notesHeight = notesLines.length * 3 + 6;
      doc.rect(margin, yPosition, contentWidth, notesHeight, 'F');

      yPosition += 3;
      doc.setFontSize(fontSize.small);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text('Notes:', margin + 3, yPosition);
      yPosition += 3;

      doc.setFont('helvetica', 'normal');
      doc.text(notesLines, margin + 3, yPosition);
      yPosition += notesLines.length * 3 + 4;
    }

    // ========== RENDER SECTIONS FROM CONFIG ==========
    const sectionOrder = Object.keys(config.sections);
    let sectionNumber = 1;

    for (const sectionKey of sectionOrder) {
      const sectionConfig = config.sections[sectionKey];
      const dataKey = sectionDataKeyMap[sectionKey] || sectionKey;
      let items = sessionData[dataKey];

      // Normalize to array
      if (items && !Array.isArray(items)) {
        items = [items];
      }

      // Skip if no items
      if (!items || items.length === 0) continue;

      // Check if section gate question was answered "No"
      const gateQuestion = sectionConfig.questions[sectionConfig.initialQuestion];
      if (gateQuestion?.type === 'boolean') {
        const gateField = gateQuestion.field || sectionConfig.initialQuestion;
        const gateAnswer = items[0]?.[gateField];
        if (gateAnswer === false) continue; // Skip entire section
      }

      // Get answered questions for first item to check if section has content
      const firstItemAnswers = getAnsweredQuestionsForReport(config, sectionKey, items[0]);
      if (firstItemAnswers.length === 0) continue;

      // Render section heading
      addSectionHeading(`${sectionNumber}. ${sectionConfig.label.toUpperCase()}`);
      sectionNumber++;

      // Render each item
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const answeredQuestions = getAnsweredQuestionsForReport(config, sectionKey, item);

        if (answeredQuestions.length === 0) continue;

        // Item name for looped sections with multiple items
        if (sectionConfig.type === 'looped') {
          const nameField = itemNameFieldMap[sectionKey];
          const itemName = nameField ? item[nameField] : null;
          if (itemName || items.length > 1) {
            addItemHeading(itemName || `Item ${i + 1}`);
          }
        }

        // Render each answered question
        for (const questionData of answeredQuestions) {
          const { question, answer, answeredFields, itemData, type } = questionData;

          // Boolean "Yes" - render as checkmark indicator
          if (type === 'boolean_yes') {
            addCheckmark(question.text);
          }
          // Grouped form - render fields
          else if (type === 'grouped_form' && answeredFields) {
            await addGroupedFormFields(question, answeredFields, itemData || item);
          }
          // Standalone image field
          else if (type === 'image' && Array.isArray(answer) && answer.length > 0) {
            checkPageBreak(8);
            doc.setFontSize(fontSize.small);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(80, 80, 80);
            doc.text(question.text || '', margin + 3, yPosition);
            yPosition += 3;
            yPosition = await addImagesToDocument(
              doc, answer, yPosition, margin + 5,
              contentWidth - 5, pageHeight, checkPageBreak
            );
          }
          // Text/elevation/other - simple label: value
          else if (answer !== undefined && answer !== null && answer !== '') {
            // Don't show text fields that are just names (already shown as item heading)
            const isNameField = itemNameFieldMap[sectionKey] === question.field;
            // Skip if it looks like image data (array of objects with url OR plain base64 strings)
            const isImageData = Array.isArray(answer) && answer.length > 0 &&
              (answer[0]?.url || (typeof answer[0] === 'string' && answer[0].startsWith('data:image')));
            if (!isNameField && !isImageData) {
              addLabeledValue(question.text, answer);
            } else if (isImageData) {
              // Handle image data that wasn't caught by type check
              checkPageBreak(8);
              doc.setFontSize(fontSize.small);
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(80, 80, 80);
              doc.text(question.text || '', margin + 3, yPosition);
              yPosition += 3;
              yPosition = await addImagesToDocument(
                doc, answer, yPosition, margin + 5,
                contentWidth - 5, pageHeight, checkPageBreak
              );
            }
          }
        }

        yPosition += 2; // Small spacing between items
      }

      yPosition += 3; // Spacing between sections
    }

    // ========== FOOTER ON EACH PAGE ==========
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(fontSize.footer);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${totalPages} | Model Planner Export | ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' }
      );
    }

    // ========== SAVE PDF ==========
    const jobNumber = project?.jobNumber || project?.job_number || 'UNKNOWN';
    const projectName = (project?.name || project?.projectName || 'Untitled')
      .replace(/[^a-z0-9]/gi, '_')
      .substring(0, 40);
    const fileName = `${jobNumber}-${projectName}-Planner_Summary.pdf`;

    doc.save(fileName);
  }
}

const modelPlannerExportService = new ModelPlannerExportService();
export default modelPlannerExportService;
